import { useNavigate } from "react-router-dom";
import { useCinematic } from "@/data/cinematicMode/context";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, RotateCcw, X, PanelRight, Film } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useRef } from "react";

export function SceneDirectorBar() {
  const {
    state, currentScene, scenes,
    nextScene, prevScene, replayScene, stopCinematic, toggleNarrator, goToScene,
  } = useCinematic();
  const navigate = useNavigate();
  const lastSceneIndexRef = useRef<number>(-1);

  // Navigate to scene route when scene changes — keyed on index, not route
  useEffect(() => {
    if (state.active && state.currentSceneIndex !== lastSceneIndexRef.current) {
      lastSceneIndexRef.current = state.currentSceneIndex;
      navigate(currentScene.route, { replace: true });
    }
  }, [state.active, state.currentSceneIndex, currentScene.route, navigate]);

  if (!state.active) return null;

  const isFirst = state.currentSceneIndex === 0;
  const isLast = state.currentSceneIndex === scenes.length - 1;
  const completedCount = Object.keys(state.completedScenes).length;

  return (
    <div className="sticky top-0 z-[60] border-b bg-card/95 backdrop-blur-md">
      {/* Progress track */}
      <div className="h-0.5 bg-muted">
        <div
          className="h-full bg-primary transition-all duration-700 ease-out"
          style={{ width: `${((state.currentSceneIndex + 1) / scenes.length) * 100}%` }}
        />
      </div>

      <div className="px-4 md:px-6 py-2.5 flex items-center gap-4">
        {/* Film icon + scene counter */}
        <div className="flex items-center gap-2 shrink-0">
          <Film className="h-4 w-4 text-primary" />
          <Badge variant="outline" className="text-[10px] font-mono h-5 px-1.5">
            {state.currentSceneIndex + 1} / {scenes.length}
          </Badge>
        </div>

        {/* Scene title + narration */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <h2 className="text-sm font-semibold text-foreground truncate">
              {currentScene.title}
            </h2>
            <span className="text-[11px] text-muted-foreground hidden sm:inline">
              {currentScene.subtitle}
            </span>
          </div>
          <p className="text-xs text-muted-foreground/80 truncate mt-0.5 max-w-2xl">
            {currentScene.narration}
          </p>
        </div>

        {/* Scene dots */}
        <div className="hidden lg:flex items-center gap-1 shrink-0">
          {scenes.map((s, i) => (
            <button
              key={s.id}
              onClick={() => goToScene(i)}
              className={cn(
                "h-1.5 rounded-full transition-all duration-300",
                i === state.currentSceneIndex
                  ? "w-4 bg-primary"
                  : state.completedScenes[s.id]
                    ? "w-1.5 bg-primary/40"
                    : "w-1.5 bg-muted-foreground/20"
              )}
            />
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost" size="icon"
            className="h-7 w-7"
            disabled={isFirst}
            onClick={prevScene}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost" size="icon"
            className="h-7 w-7"
            onClick={replayScene}
            title="Replay scene"
          >
            <RotateCcw className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost" size="icon"
            className="h-7 w-7"
            disabled={isLast}
            onClick={nextScene}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          <div className="w-px h-5 bg-border mx-1" />

          <Button
            variant="ghost" size="icon"
            className="h-7 w-7"
            onClick={toggleNarrator}
            title={state.narratorOpen ? "Hide narrator" : "Show narrator"}
          >
            <PanelRight className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost" size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            onClick={stopCinematic}
            title="Exit demo"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
